import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, buildImageUrl } from "@/lib/api";
import { useApp } from "@/contexts/AppContext";
import { formatPrice } from "@/lib/currency";
import { Button } from "@/components/ui/button";
import { MessageCircle, FileText, ArrowLeft } from "lucide-react";

export default function ProductDetailPage() {
  const { id } = useParams();
  const { currency, settings, tr, lang } = useApp();
  const [product, setProduct] = useState(null);
  const [activeImg, setActiveImg] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/products/${id}`).then((r) => {
      setProduct(r.data);
      document.title = `${r.data.name} | AFC TELECOM`;
    }).finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="max-w-7xl mx-auto px-4 py-20 text-center text-slate-400">{tr.common.loading}</div>;
  if (!product) return <div className="max-w-7xl mx-auto px-4 py-20 text-center text-slate-500">Not found</div>;

  const waLink = `https://wa.me/${settings.whatsapp_sales}?text=${encodeURIComponent(tr.whatsappMsgs.product(product.name))}`;
  const quoteLink = `/request-quote?product=${encodeURIComponent(product.name)}&id=${product.id}`;
  const displayName = lang === "ar" && product.name_ar ? product.name_ar : product.name;
  const displayDesc = lang === "ar" && product.description_ar ? product.description_ar : product.description;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-16">
      <Link to={product.type === "accessory" ? "/accessories" : "/phones"} className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-[#1E5FD9] mb-6" data-testid="back-link">
        <ArrowLeft className="w-4 h-4" /> Back
      </Link>

      <div className="grid lg:grid-cols-2 gap-10 lg:gap-16">
        <div>
          <div className="bg-slate-50 rounded-3xl aspect-square flex items-center justify-center p-10 border border-slate-100">
            {product.images?.[activeImg] ? (
              <img src={buildImageUrl(product.images[activeImg])} alt={displayName} className="max-h-full max-w-full object-contain" />
            ) : (
              <div className="text-slate-300">No image</div>
            )}
          </div>
          {product.images?.length > 1 && (
            <div className="mt-4 flex gap-2">
              {product.images.map((im, i) => (
                <button key={i} onClick={() => setActiveImg(i)} className={`w-20 h-20 rounded-xl border overflow-hidden ${activeImg === i ? "border-[#1E5FD9]" : "border-slate-200"}`}>
                  <img src={buildImageUrl(im)} alt="" className="w-full h-full object-contain bg-slate-50" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#1E5FD9]">{product.brand}</p>
          <h1 className="mt-2 font-display text-3xl sm:text-4xl font-bold text-[#0B132B]" data-testid="product-name">{displayName}</h1>
          <div className="mt-4 flex items-center gap-3">
            <div className="text-3xl font-bold text-[#1E5FD9] font-display" data-testid="product-price">{formatPrice(product.price, currency, settings)}</div>
            <span className="text-xs px-3 py-1 rounded-full bg-slate-100 text-slate-700">{tr.availability[product.availability] || product.availability}</span>
          </div>

          {displayDesc && <p className="mt-6 text-slate-600 leading-relaxed">{displayDesc}</p>}

          <div className="mt-8 space-y-4">
            {product.storage_options?.length > 0 && (
              <div>
                <p className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-500 mb-2">{tr.common.storage}</p>
                <div className="flex flex-wrap gap-2">
                  {product.storage_options.map((s) => <span key={s} className="px-3 py-1.5 rounded-full border border-slate-200 text-sm">{s}</span>)}
                </div>
              </div>
            )}
            {product.colors?.length > 0 && (
              <div>
                <p className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-500 mb-2">{tr.common.color}</p>
                <div className="flex flex-wrap gap-2">
                  {product.colors.map((c) => <span key={c} className="px-3 py-1.5 rounded-full border border-slate-200 text-sm">{c}</span>)}
                </div>
              </div>
            )}
            <div className="flex flex-wrap gap-6 pt-2 text-sm">
              <div><span className="text-slate-500">{tr.common.model}:</span> <span className="font-medium">{product.model}</span></div>
              <div><span className="text-slate-500">{tr.common.condition}:</span> <span className="font-medium">{product.condition}</span></div>
            </div>
          </div>

          {product.specifications && Object.keys(product.specifications).length > 0 && (
            <div className="mt-8">
              <h3 className="font-display text-lg font-semibold mb-3">{tr.common.specifications}</h3>
              <div className="border border-slate-200 rounded-2xl overflow-hidden">
                {Object.entries(product.specifications).map(([k, v], i) => (
                  <div key={k} className={`flex justify-between px-4 py-3 text-sm ${i % 2 ? "bg-slate-50" : "bg-white"}`}>
                    <span className="text-slate-500">{k}</span>
                    <span className="font-medium text-[#0B132B] text-end">{String(v)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild className="rounded-full bg-[#1E5FD9] hover:bg-[#174AB0] px-6 py-6">
              <Link to={quoteLink} data-testid="pd-request-quote">
                <FileText className="w-4 h-4 me-2" /> {tr.common.requestQuote}
              </Link>
            </Button>
            <Button asChild className="rounded-full bg-[#25D366] hover:bg-[#1EBE57] px-6 py-6">
              <a href={waLink} target="_blank" rel="noopener noreferrer" data-testid="pd-whatsapp">
                <MessageCircle className="w-4 h-4 me-2" /> {tr.common.whatsapp}
              </a>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
